/*
 * File: c:\Users\j.mazat\Desktop\Autobuy\AutobuyBackOffice\src\Content\Message\Message.tsx
 * Project: c:\Users\j.mazat\Desktop\Autobuy\AutobuyBackOffice
 * Created Date: Tuesday August 1st 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Thursday, 9th November 2023 04:28:44
 * Modified By: Simon (s.gourlet@autobuy.fr>)
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 */

import { useEffect, useRef, useState } from "react";
import Styles from "Content/Message/Message.module.css";
import TinyEditor, { TinyEditorHandle } from "Content/TinyMCE/TinyEditor";
import { useLoaderData } from "react-router-dom";
import translator from "MultiLanguage/TranslationWrapper";
import { MessageType } from "Type/message.type";
import TitlePage from "Content/HeadingPage/TitlePage/TitlePage";
import RouterPathResolver from "Config/Router/RouterPathResolver";
import ModalMessage from "Content/Modals/ModalMessage/ModalMessage";
import MessageService from "Services/Message/MessageService";
import ModalConfirmation from "Content/Modals/ModalConfirmation/ModalConfirmation";
import LiCustom from "Content/LiCustom/LiCustom";
import { ModalConfirmRequest } from "Content/Modals/ModalConfirmRequest/ModalConfirmRequest";

function EditMessage() {
    const loadata: MessageType[] = useLoaderData() as MessageType[];
    const [myTrueData, setMyTrueData] = useState<MessageType[] | null>(loadata.length !== 0 ? [...loadata] : null);
    const [messageName, setMessageName] = useState<string | null>(loadata.length !== 0 ? loadata[0].Name : null);
    const [messageID, setMessageID] = useState<string | null>(loadata.length !== 0 ? loadata[0].id : null);
    const [messageToDisplay, setMessageToDisplay] = useState<string>(loadata.length !== 0 && loadata[0].Content ? loadata[0].Content : "");
    const [showModalMessage, setShowModalMessage] = useState<boolean>(false);
    const [modalConfig, setModalConfig] = useState<{show: boolean, title: string, additionalMessage?: string, request: () => void }>({ show: false, title: "", request: () => {} });
    const [newTemplate, setNewTemplate] = useState<MessageType | undefined>();
    const [isOkay, setIsOkay] = useState<boolean>(false);
    const [confirmModal, setConfirmModal] = useState<boolean>(false);
    const [dataUpdated, setDataUpdated] = useState<string | null>(null);
    const editorRef = useRef<TinyEditorHandle>(null);

    useEffect(() => {
        if (newTemplate) {
            setMessageName(newTemplate.Name);
            setMessageID(newTemplate.id);
            setMessageToDisplay(newTemplate.Content ?? "");
            setDataUpdated(null);
            if(myTrueData === null) {
                setMyTrueData([newTemplate]);
            } else {
                setMyTrueData([...myTrueData, newTemplate]);
            }
            setNewTemplate(undefined);
        }
    }, [newTemplate]);

    useEffect(() => {
        if (dataUpdated != null && myTrueData) {
            let idxToDel: number | undefined;
            myTrueData.forEach((item, index) => { if (item.Name === messageName) {idxToDel = index} });
            if (idxToDel) {
                myTrueData[idxToDel].Content = dataUpdated;
                setDataUpdated(null);
                setMyTrueData([...myTrueData]);
            }
        }
    }, [dataUpdated]);

    const handleSaveMessage = () => {
        const content = editorRef.current?.getContent();
        if (messageID && content) {
            MessageService.UpdateMessage(messageID, content)
            .then(() => {
              setIsOkay(true);
              setDataUpdated(content);
              setConfirmModal(true);
            })
            .catch(() => {
                setIsOkay(false);
                setConfirmModal(true);
            });
        }
    };

    const handleDeleteMessage = () => {
        if (messageID) {
            MessageService.DeleteMessage(messageID)
            .then(() => {
                setIsOkay(true);
                setMessageName(null);
                setMessageID(null);
                setMessageToDisplay("");
                setDataUpdated(null);
                if (myTrueData) {
                    let idxToDel: number | undefined;
                    myTrueData.forEach((item, index) => { if (item.Name === messageName) {idxToDel = index} });
                    if (idxToDel !== undefined && idxToDel >= 0) {
                        myTrueData.splice(idxToDel, 1);
                        setMyTrueData([...myTrueData]);
                        var data: MessageType | null = null;
                        if (idxToDel !== 0) {
                            data = myTrueData[idxToDel - 1];
                        } else if (myTrueData.length > 0)
                            data =  myTrueData[0];
                        if (data != null) {
                            setMessageName(data.Name);
                            setMessageID(data.id);
                            setMessageToDisplay(data.Content ?? "");
                        } else {
                            setMessageName(null);
                            setMessageID(null);
                            setMessageToDisplay("");
                        }
                    }
                }
            })
            .catch(() => {
                setIsOkay(false);
            });
            setConfirmModal(true);
        }
    };

    const handleDataToDisplay = (messageName :string) => {
        let tmp = myTrueData?.filter((val) => val.Name === messageName);
        if (tmp && tmp.length > 0) {
            setMessageName(tmp[0].Name);
            setMessageID(tmp[0].id);
            if (tmp[0].Content)
                setMessageToDisplay(tmp[0].Content);
            else
                setMessageToDisplay("");
        }
    };

    const closeMessageModal = () => {
        setShowModalMessage(false);
    };

    const openConfirmRequestModal = (title: string, request: () => void, additionalMessage?: string,) => {
        setModalConfig({
        show: true,
        title,
        additionalMessage,
        request,
      });
    };

    const closeConfirmRequestModal = () => {
      setModalConfig((prev) => ({ ...prev, show: false }));
    };

    const closeConfirmModal = () => {
        setConfirmModal(false);
        closeConfirmRequestModal();
    }

    return(
        <div className="p-3">
            <TitlePage title={translator.decode("dynamic.editMessage")} backLink={RouterPathResolver.pathHome.path} action={<button className="btn btn-primary" onClick={() => setShowModalMessage(true)}>{translator.decode("dynamic.create")}</button>} />
            <div className={`${Styles.messageHolder}`}>
                <LiCustom data={myTrueData ? myTrueData?.map(val => val.Name).sort((v1: string, v2: string) => v1.localeCompare(v2)) : null} onClick={handleDataToDisplay} title={translator.decode("dynamic.message")} />
                <TinyEditor ref={editorRef} handleSaveMessage={() => openConfirmRequestModal(translator.decode(`dynamic.save`), handleSaveMessage)} displayMenuBar messageToDisplay={messageToDisplay} messageName={messageName} id={messageID} deleteMessage={() => openConfirmRequestModal(translator.decode('dynamic.delete'), handleDeleteMessage)} setDataUpdated={setDataUpdated} />
            </div>
            <ModalMessage show={showModalMessage} closeModal={closeMessageModal} title="" onClose={closeMessageModal} autoClose={closeMessageModal} addNewTemplate={setNewTemplate} />
            <ModalConfirmation show={confirmModal} onClose={closeConfirmModal} closeModal={closeConfirmModal} isOkay={isOkay} />
            <ModalConfirmRequest show={modalConfig.show} closeModal={closeConfirmRequestModal} title={modalConfig.title} onClose={closeConfirmRequestModal} request={modalConfig.request} additionnalMessage={modalConfig.additionalMessage} />
        </div>
    )
}

export default EditMessage;
